import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import * as Service from '../services/ApplyCode';
import type {
  IApplyCodeErrorBody,
  IApplyCodeParams,
  IApplyCodeResult,
} from '../types/DiscountCode.types';
import { discountCodeKeys } from './useGetDiscountCodes';

/**
 * Applies a code to the current office and refreshes the history list so the new
 * card appears without a manual refetch. A re-apply (`created: false`) refreshes
 * too — the BE upserts, so the list comes back with the same single row.
 *
 * Errors are typed as `AxiosError<IApplyCodeErrorBody>` and deliberately NOT
 * swallowed here: the dialog needs the `reason` to tell its three distinct
 * failure states apart, and it owns the error display.
 *
 * @param officeId - the location Billing Details was opened for; the selected office when omitted
 */
export const useApplyCode = (officeId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<IApplyCodeResult, AxiosError<IApplyCodeErrorBody>, IApplyCodeParams>({
    mutationFn: (params) => Service.applyCode(params, officeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountCodeKeys.all });
    },
    meta: { skipGlobalErrorToaster: true },
  });
};
